import { Injectable } from '@angular/core';
import { WASI, WASIProcExit } from '@bjorn3/browser_wasi_shim';
import { PreopenDirectory } from '@bjorn3/browser_wasi_shim';
import { Fd } from '@bjorn3/browser_wasi_shim';
import { File as WasiFile } from '@bjorn3/browser_wasi_shim';

/**
 * Clase para capturar la salida estándar de ExifTool
 */
class CustomFd extends Fd {
  private collectedOutput = '';

  fd_write(data: Uint8Array): { ret: number; nwritten: number } {
    const text = new TextDecoder().decode(data);
    this.collectedOutput += text;
    return { ret: 0, nwritten: data.length };
  }

  getOutput(): string {
    return this.collectedOutput;
  }
}

export interface ParsedOutput {
  label: string;
  value: string;
}

@Injectable({
  providedIn: 'root',
})
export class WasiExiftoolService {
  private wasmModule: ArrayBuffer | null = null;
  private BASE_URL = 'https://sam2-model-download.b-cdn.net';
  private zeroPerlUrl = `${this.BASE_URL}/zeroperl.wasm`;
  
  constructor() {}

  /**
   * Inicializa el módulo WASM de ExifTool
   */
  private async initWasmModule(): Promise<ArrayBuffer> {
    if (this.wasmModule) {
      return this.wasmModule;
    }

    // Cargar desde la red directamente (evitamos caché por problemas de API)
    const response = await fetch(this.zeroPerlUrl);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch zeroperl.wasm: ${response.status}`);
    }
    
    this.wasmModule = await response.arrayBuffer();
    return this.wasmModule;
  }

  /**
   * Procesa una imagen con ExifTool completo usando WASI para extraer todos los metadatos
   * @param browserFile Archivo de imagen a procesar
   * @returns Metadatos completos formateados
   */
  public async extractFullMetadata(browserFile: globalThis.File): Promise<ParsedOutput[]> {
    try {
      console.log('[ExifTool Debug] Iniciando procesamiento de archivo:', browserFile.name, 'tipo:', browserFile.type, 'tamaño:', browserFile.size);
      
      const fileName = browserFile.name;
      const imageData = await browserFile.arrayBuffer();
      console.log('[ExifTool Debug] Array buffer obtenido, tamaño:', imageData.byteLength);

      // Script de Perl simplificado que funciona correctamente (basado en la implementación experimental)
      const perlScript = `
        use Image::ExifTool;
        
        print "=== EXIFTOOL DEBUG START ===\\n";
        print "Iniciando ExifTool con Perl " . $] . "\\n";
        print "Versión ExifTool: " . $Image::ExifTool::VERSION . "\\n";
        print "Procesando archivo: ${fileName}\\n";
        
        # Crear nueva instancia con configuración efectiva
        my $exif = Image::ExifTool->new();
        $exif->Options(
            Unknown => 2,          # Mostrar tags desconocidos (nivel 2)
            Duplicates => 0,       # No mostrar duplicados para ahorrar memoria
            Binary => 1,           # Incluir datos binarios (necesario para C2PA)
            Escape => 1,           # Escapar caracteres especiales HTML
            Validate => 1,         # Validar firmas C2PA
            Composite => 2         # Calcular valores compuestos (nivel 2)
        );
        print "Opciones de ExifTool configuradas\\n";

        # Procesar la imagen
        print "Comenzando a procesar imagen...\\n";
        my $info = $exif->ImageInfo("${fileName}");
        print "Imagen procesada\\n";
        
        # Manejar errores
        if (my $err = $exif->GetValue("Error")) {
            print "ERROR ENCONTRADO: $err\\n";
        } else {
            print "No se encontraron errores de procesamiento\\n";
            
            # Comprobar existencia de tags específicos de C2PA
            print "=== VERIFICANDO DATOS C2PA ===\\n";
            
            # Verificar específicamente si es una imagen generada por IA
            if (exists $info->{'C2PA:ClaimGenerator'}) {
                print "Encontrado: C2PA:ClaimGenerator = " . $info->{'C2PA:ClaimGenerator'} . "\\n";
            } else {
                print "No encontrado: C2PA:ClaimGenerator\\n";
            }
            
            if (exists $info->{'C2PA:ActionsDigitalSourceType'}) {
                print "Encontrado: C2PA:ActionsDigitalSourceType = " . $info->{'C2PA:ActionsDigitalSourceType'} . "\\n";
                if ($info->{'C2PA:ActionsDigitalSourceType'} =~ /trainedAlgorithmicMedia|algorithmicMedia|generative/i) {
                    print "ImagenGeneradaPorIA: SÍ\\n";
                }
            } else {
                print "No encontrado: C2PA:ActionsDigitalSourceType\\n";
            }
            
            # Verificar datos de IA sin prefijo C2PA
            if (exists $info->{'ActionsDigitalSourceType'}) {
                print "Encontrado: ActionsDigitalSourceType (sin prefijo C2PA) = " . $info->{'ActionsDigitalSourceType'} . "\\n";
                if ($info->{'ActionsDigitalSourceType'} =~ /trainedAlgorithmicMedia|algorithmicMedia|generative/i) {
                    print "ImagenGeneradaPorIA: SÍ\\n";
                }
            } else {
                print "No encontrado: ActionsDigitalSourceType (sin prefijo C2PA)\\n";
            }
            
            # Verificar nombre del software de IA
            if (exists $info->{'C2PA:ActionsSoftwareAgentName'}) {
                print "Encontrado: C2PA:ActionsSoftwareAgentName = " . $info->{'C2PA:ActionsSoftwareAgentName'} . "\\n";
                print "SoftwareDeIA: " . $info->{'C2PA:ActionsSoftwareAgentName'} . "\\n";
            } elsif (exists $info->{'ActionsSoftwareAgentName'}) {
                print "Encontrado: ActionsSoftwareAgentName (sin prefijo C2PA) = " . $info->{'ActionsSoftwareAgentName'} . "\\n";
                print "SoftwareDeIA: " . $info->{'ActionsSoftwareAgentName'} . "\\n";
            } else {
                print "No encontrado: ActionsSoftwareAgentName\\n";
            }
            
            # Verificar tags generales de C2PA
            print "=== VERIFICANDO CUALQUIER TAG C2PA ===\\n";
            my $c2paFound = 0;
            foreach my $tag (sort keys %$info) {
                if ($tag =~ /C2PA|Claim|Actions|Assertion|Verification|Validation/i) {
                    print "C2PA relacionado: $tag = $info->{$tag}\\n";
                    $c2paFound = 1;
                }
            }
            
            if (!$c2paFound) {
                print "ADVERTENCIA: No se encontraron etiquetas relacionadas con C2PA\\n";
                
                # Listar todas las etiquetas disponibles para diagnóstico
                print "=== LISTANDO TODAS LAS ETIQUETAS DISPONIBLES ===\\n";
                my $count = 0;
                foreach my $tag (sort keys %$info) {
                    $count++;
                    if ($count <= 20) { # Limitar a 20 para no sobrecargar la salida
                        print "Tag disponible: $tag\\n";
                    }
                }
                print "Total de tags: $count\\n";
            }
            
            # Imprimir todos los metadatos ordenados alfabéticamente
            print "=== IMPRIMIENDO TODOS LOS METADATOS ===\\n";
            foreach my $tag (sort keys %$info) {
                my $val = $info->{$tag};
                if (defined $val) {
                    if (ref($val) eq 'ARRAY') {
                        $val = join(', ', @$val);
                    }
                    print "$tag: $val\\n";
                }
            }
        }
        
        print "=== EXIFTOOL DEBUG END ===\\n";
      `;

      console.log('[ExifTool Debug] Script Perl preparado:', perlScript.length, 'caracteres');

      const stdout = new CustomFd();
      const stderr = new CustomFd();

      // Configuración WASI con valores moderados que funcionan
      console.log('[ExifTool Debug] Configurando instancia WASI');
      const wasi = new WASI(
        ['perl', '-e', perlScript],
        ['LC_ALL=C', 'PERL_UNICODE=SD'],
        [
          new CustomFd(), // stdin (fd 0)
          stdout, // stdout (fd 1)
          stderr, // stderr (fd 2)
          new PreopenDirectory('/dev', new Map([['null', new WasiFile(new Uint8Array())]])),
          new PreopenDirectory('.', new Map([[fileName, new WasiFile(new Uint8Array(imageData))]])),
        ],
        {
          debug: true, // Activar debug para más información
        }
      );

      // Configuración de memoria basada en la implementación que funciona
      console.log('[ExifTool Debug] Configurando memoria WASM');
      const imports = {
        wasi_snapshot_preview1: wasi.wasiImport,
        env: {
          memory: new WebAssembly.Memory({
            initial: 100,  // 6.4MB iniciales
            maximum: 1000, // 64MB máximo
            shared: false,
          }),
        },
      };

      console.log('[ExifTool Debug] Cargando módulo WASM');
      const wasmBuffer = await this.initWasmModule();
      console.log('[ExifTool Debug] Módulo WASM cargado, tamaño:', wasmBuffer.byteLength);
      
      console.log('[ExifTool Debug] Instanciando WebAssembly');
      const { instance } = await WebAssembly.instantiate(wasmBuffer, imports);
      console.log('[ExifTool Debug] WebAssembly instanciado correctamente');

      console.log('[ExifTool Debug] Iniciando ejecución');
      try {
        wasi.start(instance as any);
        console.log('[ExifTool Debug] Ejecución completada exitosamente');
      } catch (e) {
        if (e instanceof WASIProcExit) {
          console.log(`[ExifTool Debug] ExifTool terminó con código: ${e.code}`);
          if (e.code !== 0) {
            const stderrOutput = stderr.getOutput();
            console.error('[ExifTool Error] Salida de stderr:', stderrOutput);
            throw new Error(`ExifTool exited with code ${e.code}: ${stderrOutput}`);
          }
        } else {
          console.error('[ExifTool Error] Error desconocido:', e);
          throw e;
        }
      }

      const output = stdout.getOutput();
      console.log('[ExifTool Debug] Salida obtenida, longitud:', output.length);
      console.log('[ExifTool Debug] Primeros 500 caracteres de la salida:', output.substring(0, 500));
      console.log('[ExifTool Debug] Últimos 500 caracteres de la salida:', output.substring(output.length - 500));
      
      if (output.trim().length === 0) {
        console.error('[ExifTool Error] No se generó salida');
        throw new Error('ExifTool no generó salida');
      }
      
      const parsedOutput = this.parseExifOutput(output);
      console.log('[ExifTool Debug] Datos parseados, número de elementos:', parsedOutput.length);
      
      // Verificar si se encontraron datos específicos de IA
      const iaDetection = parsedOutput.find(item => item.label === 'ImagenGeneradaPorIA');
      console.log('[ExifTool Debug] Detección de IA:', iaDetection ? 'SÍ' : 'NO');
      
      return parsedOutput;
    } catch (error) {
      console.error('[ExifTool Error] Error general:', error);
      throw new Error(`Failed to run ExifTool: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Parsea la salida de texto de ExifTool a un formato estructurado
   */
  private parseExifOutput(text: string): ParsedOutput[] {
    // Sin logs de depuración para evitar sobrecarga
    
    // Buscar sección de depuración
    const debugStartIndex = text.indexOf('=== EXIFTOOL DEBUG START ===');
    const debugEndIndex = text.indexOf('=== EXIFTOOL DEBUG END ===');
    
    // Implementación simplificada basada en la versión que funciona
    const lines = text.split('\n')
      .filter(line => line.includes(':') && !line.startsWith('==='));
      
    // Buscar indicadores específicos de C2PA y AI en el texto completo
    const hasC2PAInfo = text.includes('C2PA:') || 
                        text.includes('Claim') || 
                        text.includes('Validation') ||
                        text.includes('ActionsDigitalSourceType');
                        
    const isAIGenerated = text.includes('ImagenGeneradaPorIA: SÍ') || 
                          text.includes('trainedAlgorithmicMedia') ||
                          text.includes('algorithmicMedia');
                          
    const softwareMatch = text.match(/SoftwareDeIA: ([^\n]+)/);
    const aiSoftware = softwareMatch ? softwareMatch[1] : null;
    
    const result = lines.map(line => {
      // Separar por el primer ':' ya que algunos valores también contienen ':'
      const colonIndex = line.indexOf(':');
      if (colonIndex === -1) {
        return null;
      }
      
      const label = line.substring(0, colonIndex).trim();
      const value = line.substring(colonIndex + 1).trim();
      
      return {
        label,
        value: value || '',
      };
    }).filter(item => item !== null) as ParsedOutput[];
    
    // Agregar indicadores específicos si se encontraron en el texto pero no están en el resultado
    if (hasC2PAInfo && !result.some(item => 
        item.label.includes('C2PA') || 
        item.label.includes('Claim') || 
        item.label.includes('Validation'))) {
      result.push({
        label: 'C2PADetected',
        value: 'true',
      });
    }
    
    if (isAIGenerated && !result.some(item => item.label === 'ImagenGeneradaPorIA')) {
      result.push({
        label: 'ImagenGeneradaPorIA',
        value: 'SÍ',
      });
      
      if (aiSoftware && !result.some(item => item.label === 'SoftwareDeIA')) {
        result.push({
          label: 'SoftwareDeIA',
          value: aiSoftware,
        });
      }
    }
    
    return result;
  }
  
  /**
   * Analiza los metadatos para detectar posibles signos de manipulación o contenido generado por IA
   */
  public analyzeForManipulation(metadata: ParsedOutput[]): { 
    hasManipulationSigns: boolean; 
    isAIGenerated: boolean;
    manipulationIndicators: string[];
    aiIndicators: string[];
  } {
    const manipulationIndicators: string[] = [];
    const aiIndicators: string[] = [];
    
    // Palabras clave para detección de manipulación
    const manipulationKeywords = [
      'photoshop', 'edited', 'modified', 'edit', 'history',
      'lightroom', 'gimp', 'illustrator', 'manipulation',
    ];
    
    // Palabras clave para detección de IA
    const aiKeywords = [
      'GPT', 'DALL-E', 'AI', 'artificial intelligence', 
      'neural', 'generated', 'algorithmic', 'OpenAI', 'Midjourney', 'Stable Diffusion',
    ];
    
    // Buscar indicadores específicos de C2PA para IA
    const c2paAiIndicators = metadata.filter(item => 
      (item.label === 'ActionsDigitalSourceType' && 
       item.value.includes('trainedAlgorithmicMedia')) ||
      (item.label === 'ActionsSoftwareAgentName' && 
       /GPT|OpenAI|DALL-E|Midjourney|AI/.test(item.value))
    );
    
    if (c2paAiIndicators.length > 0) {
      c2paAiIndicators.forEach(indicator => {
        aiIndicators.push(`C2PA: ${indicator.label} = ${indicator.value}`);
      });
    }
    
    // Buscar indicadores de software de edición
    const softwareIndicators = metadata.filter(item => 
      item.label === 'Software' || 
      item.label === 'ProcessingSoftware' || 
      item.label === 'CreatorTool'
    );
    
    softwareIndicators.forEach(software => {
      const softwareValue = software.value.toLowerCase();
      
      // Verificar software de manipulación
      if (manipulationKeywords.some(keyword => softwareValue.includes(keyword.toLowerCase()))) {
        manipulationIndicators.push(`Software de edición detectado: ${software.value}`);
      }
      
      // Verificar software de IA
      if (aiKeywords.some(keyword => softwareValue.includes(keyword.toLowerCase()))) {
        aiIndicators.push(`Software de IA detectado: ${software.value}`);
      }
    });
    
    // Verificar fechas
    const creationDate = metadata.find(item => 
      item.label === 'CreateDate' || item.label === 'DateTimeOriginal');
    const modifyDate = metadata.find(item => 
      item.label === 'ModifyDate' || item.label === 'LastModified');

    if (creationDate && modifyDate) {
      try {
        const createTime = new Date(creationDate.value).getTime();
        const modifyTime = new Date(modifyDate.value).getTime();
        
        if (!isNaN(createTime) && !isNaN(modifyTime)) {
          const diffMinutes = Math.abs(modifyTime - createTime) / (1000 * 60);
          
          if (diffMinutes > 5) {
            manipulationIndicators.push(`Tiempo entre creación y modificación: ${Math.round(diffMinutes)} minutos`);
          }
        }
      } catch (e) {
        // Error al procesar fechas, ignorar
      }
    }
    
    // Buscar datos C2PA eliminados o alterados
    if (metadata.some(item => 
        item.label.includes('ValidationResults') && 
        !item.value.includes('validated'))) {
      manipulationIndicators.push('Validación C2PA fallida o incompleta');
    }
    
    // Verificar datos EXIF incompletos
    const hasMake = metadata.some(item => item.label === 'Make');
    const hasModel = metadata.some(item => item.label === 'Model');

    if (!hasMake || !hasModel) {
      manipulationIndicators.push('Falta información del dispositivo (Make/Model)');
    }
    
    return {
      hasManipulationSigns: manipulationIndicators.length > 0,
      isAIGenerated: aiIndicators.length > 0,
      manipulationIndicators,
      aiIndicators,
    };
  }
  
  /**
   * Agrupa los metadatos por categorías para mejor visualización
   */
  public groupMetadataByCategory(metadata: ParsedOutput[]): Record<string, ParsedOutput[]> {
    const groups: Record<string, ParsedOutput[]> = {
      'Camera': [],
      'Image': [],
      'GPS': [],
      'Software': [],
      'Dates': [],
      'C2PA': [], // Categoría específica para Content Credentials
      'AI': [],   // Categoría específica para indicadores de IA
      'Other': [],
    };
    
    for (const item of metadata) {
      const label = item.label.toLowerCase();
      
      if (label.includes('c2pa') || 
          label.includes('claim') || 
          label.includes('jumdf') || 
          label.includes('actions') || 
          label.includes('assertion') ||
          label.includes('manifest') ||
          label.includes('signature') ||
          label.includes('validation')) {
        groups['C2PA'].push(item);
      } else if (label.includes('gps') || 
                label.includes('latitude') || 
                label.includes('longitude')) {
        groups['GPS'].push(item);
      } else if (label.includes('camera') || 
                label.includes('make') || 
                label.includes('model') || 
                label.includes('exposure') || 
                label.includes('iso') || 
                label.includes('focal') ||
                label.includes('aperture') || 
                label.includes('flash')) {
        groups['Camera'].push(item);
      } else if (label.includes('software') || 
                label.includes('photoshop') || 
                label.includes('edit') ||
                label.includes('processed') ||
                label.includes('creator') ||
                label.includes('tool')) {
        groups['Software'].push(item);
      } else if (label.includes('date') || 
                label.includes('time') || 
                label.includes('created') || 
                label.includes('modified')) {
        groups['Dates'].push(item);
      } else if (label.includes('width') || 
                label.includes('height') || 
                label.includes('resolution') || 
                label.includes('color') || 
                label.includes('orientation') ||
                label.includes('size') ||
                label.includes('depth') ||
                label.includes('compression')) {
        groups['Image'].push(item);
      } else if (label.includes('ai') || 
                label.includes('neural') || 
                label.includes('gpt') || 
                label.includes('generated') ||
                label.includes('algorithmic') ||
                label.includes('digital source')) {
        groups['AI'].push(item);
      } else {
        groups['Other'].push(item);
      }
    }
    
    // Eliminar categorías vacías
    Object.keys(groups).forEach(key => {
      if (groups[key].length === 0) {
        delete groups[key];
      }
    });
    
    return groups;
  }
}
